import type { Organization } from '../../functions/src/types';
import { cleanS13Intake, prefillS13Intake, type S13Intake } from '../../functions/src/common/s13Workflow';
import { cleanS14Intake, prefillS14Intake, S14_SCENARIOS, type S14Intake } from '../../functions/src/common/s14Workflow';
import { cleanS16Intake, prefillS16Intake, type S16Intake } from '../../functions/src/common/s16Workflow';

export type CrisisTemplateAnswers = {
    roles: { commander: string; safety: string; pio: string; ops: string; comms: string };
    phases: Array<{ phase: string; timeframe: string; actions: string }>;
    telemetry: Array<{ method: string; frequency: string; logging: string }>;
    debrief: string;
};

export type ResiliencyTemplateAnswers = {
    functions: Array<{ name: string; rto: string; rpo: string; dependencies: string }>;
    strategies: string;
    vendors: Array<{ name: string; risk: string; mitigation: string }>;
    recovery: Array<{ phase: string; actions: string }>;
};

export type TemplateServiceRequestDraft = (
    { serviceCode: 'S-13'; intake: S13Intake } | { serviceCode: 'S-14'; intake: S14Intake } | { serviceCode: 'S-16'; intake: S16Intake }
) & { requestId?: string; attachments?: Array<{ name: string; path: string; contentType: string; size: number }> };

export type EmergencyTemplateAnswers = { scenario: string; routes: string; muster: string; equipment: string;
    checklists: Array<{ role: string; tasks: string }> };

export function missingEmergencyTemplate(templateId: string, answers: EmergencyTemplateAnswers, step?: number): string[] {
    return missingSections([
        [1, 'Template ID', !templateId.trim()], [1, 'Emergency scenario', !answers.scenario.trim()],
        [2, 'Evacuation routes', !answers.routes.trim()], [2, 'Rally points', !answers.muster.trim()],
        [3, 'Equipment requirements', !answers.equipment.trim()],
        [4, 'Response roles and duty checklists', incompleteEntries(answers.checklists)],
        [4, 'No more than 20 response roles', answers.checklists.length > 20],
    ], step);
}

const incompleteEntries = (items: Array<Record<string, string>>) => !items.length
    || items.some(item => Object.values(item).some(value => !value.trim()));

const missingSections = (sections: Array<[number, string, boolean]>, step?: number) => sections
    .filter(([at, , missing]) => missing && (step === undefined || at === step)).map(([, label]) => label);

export function missingCrisisTemplate(templateId: string, incidentType: string, severityLevel: string,
    answers: CrisisTemplateAnswers, step?: number): string[] {
    return missingSections([
        [1, 'Template ID', !templateId.trim()], [1, 'Incident type', !incidentType.trim()],
        [1, 'Severity baseline', !severityLevel.trim()],
        [2, 'Duties for each command role', Object.values(answers.roles).some(value => !value.trim())],
        [3, 'Response phases, timeframes, and actions', incompleteEntries(answers.phases)],
        [4, 'Communication methods, frequency, and logging', incompleteEntries(answers.telemetry)],
        [5, 'Debrief and recovery metrics', !answers.debrief.trim()],
    ], step);
}

export function missingResiliencyTemplate(templateId: string, incidentType: string,
    answers: ResiliencyTemplateAnswers, step?: number): string[] {
    return missingSections([
        [1, 'Template ID', !templateId.trim()], [1, 'Resiliency domain', !incidentType.trim()],
        [2, 'Critical functions, RTOs, RPOs, and dependencies', incompleteEntries(answers.functions)],
        [2, 'No more than 20 critical functions', answers.functions.length > 20],
        [3, 'Continuity strategies', !answers.strategies.trim()],
        [4, 'Vendor names, risks, and mitigations', incompleteEntries(answers.vendors)],
        [5, 'Recovery phases and actions', incompleteEntries(answers.recovery)],
    ], step);
}

const section = (label: string, content: string) => content.trim() ? `${label}\n${content.trim()}` : '';

function templateContext(sections: string[]): string {
    const context = sections.filter(Boolean).join('\n\n');
    if (context.length > 20000)
        throw new Error('Template details exceed 20,000 characters. Shorten them before opening a service request.');
    return context;
}

function recoveryHours(value: string): string {
    const match = value.trim().match(/^(\d+(?:\.\d+)?|\.\d+)\s*(hours?|hrs?|h|minutes?|mins?|m|days?|d)$/i);
    if (!match)
        return '';
    const hours = Number(match[1]) * (/^(m)/i.test(match[2]) ? 1 / 60 : /^(d)/i.test(match[2]) ? 24 : 1);
    return hours > 0 && Number.isFinite(hours) ? String(hours) : '';
}

export function crisisTemplateRequest(organization: Organization, templateId: string, incidentType: string,
    severityLevel: string, answers: CrisisTemplateAnswers): TemplateServiceRequestDraft {
    return { serviceCode: 'S-13', intake: { ...prefillS13Intake(organization), debriefProtocol: answers.debrief,
        templateContext: templateContext([
            section('Crisis Management Template', templateId), section('Incident type', incidentType),
            section('Severity baseline', severityLevel),
            section('Incident Commander duties', answers.roles.commander), section('Safety Officer duties', answers.roles.safety),
            section('Public Information Officer duties', answers.roles.pio), section('Operations Chief duties', answers.roles.ops),
            section('Communications Lead duties', answers.roles.comms),
            section('Tactical response phases', answers.phases.map(item =>
                [item.phase, item.timeframe, item.actions].filter(Boolean).join('\n')).join('\n\n')),
            section('Telemetry and communications', answers.telemetry.map(item =>
                [item.method, item.frequency, item.logging].filter(Boolean).join('\n')).join('\n\n')),
            section('Debrief and recovery metrics', answers.debrief),
        ]) } };
}

export function resiliencyTemplateRequest(organization: Organization, templateId: string, incidentType: string,
    answers: ResiliencyTemplateAnswers): TemplateServiceRequestDraft {
    const vendors = answers.vendors.map(item => [item.name, section('Risk', item.risk),
        section('Mitigation', item.mitigation)].filter(Boolean).join('\n')).join('\n\n');
    return { serviceCode: 'S-14', intake: { ...prefillS14Intake(organization),
        criticalFunctions: answers.functions.slice(0, 20).map(item => ({ name: item.name,
            rtoHours: recoveryHours(item.rto), mtpdHours: '', strategy: '',
            scenarioMitigations: Object.fromEntries(S14_SCENARIOS.map(scenario => [scenario, ''])) })),
        criticalSuppliers: vendors,
        templateContext: templateContext([
            section('Business Resiliency Template', templateId), section('Resiliency domain', incidentType),
            section('Critical functions', answers.functions.map(item => [item.name, section('Recovery time target (RTO)', item.rto),
                section('Data loss target (RPO)', item.rpo), section('Dependencies', item.dependencies)]
                .filter(Boolean).join('\n')).join('\n\n')),
            section('Continuity strategies', answers.strategies), section('Critical vendors', vendors),
            section('Recovery phases', answers.recovery.map(item =>
                [item.phase, item.actions].filter(Boolean).join('\n')).join('\n\n')),
        ]) } };
}

const draftKey = (organizationId: string, draftId: string) => `service_request_template:${organizationId}:${draftId}`;

export function emergencyTemplateRequest(organization: Organization, templateId: string,
    answers: EmergencyTemplateAnswers): TemplateServiceRequestDraft {
    const lines = (value: string) => value.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    return { serviceCode: 'S-16', intake: { ...prefillS16Intake(organization), scenario: answers.scenario,
        evacuationRoutes: lines(answers.routes), rallyPoints: lines(answers.muster), equipmentRequirements: lines(answers.equipment),
        tacticalChecklists: answers.checklists.map(item => ({ role: item.role, tasks: lines(item.tasks) })),
        templateContext: templateContext([section('Emergency Preparedness Template', templateId),
            section('Emergency scenario', answers.scenario), section('Evacuation routes', answers.routes),
            section('Rally points', answers.muster), section('Equipment requirements', answers.equipment),
            section('Role duty checklists', answers.checklists.map(item => [item.role, item.tasks].join('\n')).join('\n\n'))]) } };
}

export function storeTemplateRequest(storage: Storage, organizationId: string, draft: TemplateServiceRequestDraft,
    draftId: string = crypto.randomUUID()): string {
    storage.setItem(draftKey(organizationId, draftId), JSON.stringify(draft));
    return draftId;
}

export function readTemplateRequest(storage: Storage, organizationId: string, draftId: string): TemplateServiceRequestDraft | null {
    const value = storage.getItem(draftKey(organizationId, draftId));
    if (!value)
        return null;
    const draft = JSON.parse(value);
    if (!draft?.intake || typeof draft.intake !== 'object')
        return null;
    const saved = { requestId: typeof draft.requestId === 'string' ? draft.requestId : undefined,
        attachments: Array.isArray(draft.attachments) ? draft.attachments : [] };
    if (draft.serviceCode === 'S-13')
        return { ...saved, serviceCode: 'S-13', intake: cleanS13Intake(draft.intake) };
    if (draft.serviceCode === 'S-14')
        return { ...saved, serviceCode: 'S-14', intake: cleanS14Intake(draft.intake) };
    if (draft.serviceCode === 'S-16')
        return { ...saved, serviceCode: 'S-16', intake: cleanS16Intake(draft.intake) };
    return null;
}
